import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Divider,
    Grid2,
    Fade,
    CircularProgress,
} from '@mui/material';
import { Field, useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { TextFormField } from './FormFields/TextFormField';
import { AutoCompleteField } from './FormFields/AutoCompleteField';

// ── Pincode API ───────────────────────────────────────────────────────────────

const fetchPincodeData = async (pincode) => {
    const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
    const json = await res.json();
    if (json?.[0]?.Status === 'Success' && json[0]?.PostOffice?.length > 0) {
        const po = json[0].PostOffice[0];
        return { city: po.District, state: po.State, country: po.Country };
    }
    return null;
};

// ── Validation Schema ─────────────────────────────────────────────────────────

const validationSchema = Yup.object({
    full_name: Yup.string().required('Full name is required'),
    address_type: Yup.string().required('Address type is required'),
    address: Yup.string()
        .min(10, 'Address must be at least 10 characters')
        .required('Delivery address is required'),
    pincode: Yup.string()
        .matches(/^\d{6}$/, 'Pincode must be exactly 6 digits')
        .required('Pincode is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    country: Yup.string().required('Country is required'),
    contactNumber: Yup.string()
        .matches(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number')
        .required('Contact number is required'),
});

// ── PincodeWatcher ────────────────────────────────────────────────────────────
// Watches pincode value and auto-fetches city/state/country when 6 digits are entered

const PincodeWatcher = ({ pincode, setFieldValue, setFieldError, setPincodeLoading, setPincodeStatus }) => {
    useEffect(() => {
        if (!/^\d{6}$/.test(pincode)) {
            // Reset location fields if pincode is cleared or incomplete
            if (pincode.length < 6) {
                setFieldValue('city', '');
                setFieldValue('state', '');
                setFieldValue('country', '');
                setPincodeStatus(null);
            }
            return;
        }

        let cancelled = false;

        setPincodeLoading(true);
        setPincodeStatus(null);

        fetchPincodeData(pincode)
            .then((result) => {
                if (cancelled) return;
                if (result) {
                    setFieldValue('city', result.city);
                    setFieldValue('state', result.state);
                    setFieldValue('country', result.country);
                    setPincodeStatus('found');
                } else {
                    setFieldValue('city', '');
                    setFieldValue('state', '');
                    setFieldValue('country', '');
                    setPincodeStatus('not_found');
                    setFieldError('pincode', 'Invalid pincode – please fill manually');
                }
            })
            .catch(() => {
                if (!cancelled) setPincodeStatus('not_found');
            })
            .finally(() => {
                if (!cancelled) setPincodeLoading(false);
            });

        // Cleanup: if pincode changes before fetch resolves, ignore stale response
        return () => { cancelled = true; };
    }, [pincode]);

    return null;
};

// ── Component ─────────────────────────────────────────────────────────────────

const AddressFormDialog = ({ open, onClose, onSave, initialValues }) => {
    const [pincodeLoading, setPincodeLoading] = useState(false);
    const [pincodeStatus, setPincodeStatus] = useState(null); // 'found' | 'not_found' | null

    const formik = useFormik({
        initialValues: initialValues || {
            full_name: '',
            address: '',
            pincode: '',
            city: '',
            state: '',
            address_type: '',
            country: '',
            contactNumber: '',
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: (values) => {
            onSave(values);
            onClose();
        },
    });

    const handleClose = () => {
        formik.resetForm();
        setPincodeStatus(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            TransitionComponent={Fade}
            transitionDuration={300}
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    p: 0,
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                },
            }}
        >
            <DialogContent sx={{ px: '1.5rem', py: '1.5rem', bgcolor: '#FAFAFA' }}>
                <FormikProvider value={formik}>
                    <form onSubmit={formik.handleSubmit} noValidate>
                        {/* PincodeWatcher: reacts to pincode changes and auto-fills city/state/country */}
                        <PincodeWatcher
                            pincode={formik.values.pincode}
                            setFieldValue={formik.setFieldValue}
                            setFieldError={formik.setFieldError}
                            setPincodeLoading={setPincodeLoading}
                            setPincodeStatus={setPincodeStatus}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>

                            {/* Full Name */}
                            <Field
                                name="full_name"
                                label="Full Name"
                                placeholder="Enter your full name"
                                component={TextFormField}
                            />

                            {/* Address */}
                            <Field
                                name="address"
                                label="Delivery Address"
                                placeholder="Enter your delivery address here......"
                                multiline
                                rows={2}
                                component={TextFormField}
                            />

                          
                            <Grid2 container spacing="1rem">
                                     <Grid2 size={{ xs: 12, sm: 6 }}>
                            {/* Pincode + loading spinner */}
                            <Box sx={{ position: 'relative' }}>
                                <Field
                                    name="pincode"
                                    label="Pincode"
                                    placeholder="Enter 6-digit pin number"
                                    inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                                    component={TextFormField}
                                />
                                {pincodeLoading && (
                                    <CircularProgress
                                        size={16}
                                        sx={{
                                            color: '#DC143C',
                                            position: 'absolute',
                                            right: 12,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                        }}
                                    />
                                )}
                            </Box>
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    <Field
                                        name="city"
                                        label="City"
                                        placeholder="City"
                                        disabled={pincodeStatus === 'found'}
                                        component={TextFormField}
                                    />
                                </Grid2>
                           
                            </Grid2>

                            {/* City + State */}
                            <Grid2 container spacing="1rem">
                                                         <Grid2 size={{ xs: 12, sm: 6 }}>
                                    <Field
                                        name="state"
                                        label="State"
                                        placeholder="State"
                                        disabled={pincodeStatus === 'found'}
                                        component={TextFormField}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                                 {/* Country */}
                            <Field
                                name="country"
                                label="Country"
                                placeholder="Country"
                                disabled={pincodeStatus === 'found'}
                                component={TextFormField}
                            />
                                </Grid2>
       
                            </Grid2>

                            <Grid2 container spacing="1rem">
                                                         <Grid2 size={{ xs: 12, sm: 6 }}>
                                                               {/* Contact Number */}
                            <Field
                                name="contactNumber"
                                label="Contact Number"
                                placeholder="Enter your phone number"
                                type="tel"
                                inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                                component={TextFormField}
                            />
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                            {/* Address Type */}
                            <Field
                                name="address_type"
                                label="Address Type"
                                placeholder="Select Type"
                                component={AutoCompleteField}
                                options={[
                                    { id: 'home', label: 'Home', value: 'home' },
                                    { id: 'office', label: 'Office', value: 'office' },
                                    { id: 'other', label: 'Other', value: 'other' },
                                ]}
                            />
                                </Grid2>
       
                            </Grid2>

                            <Divider sx={{ my: '0.25rem' }} />

                            {/* Submit */}
                            <Button
                                id="add-address-submit-btn"
                                type="submit"
                                variant="contained"
                                fullWidth
                                // disabled={formik.isSubmitting}
                                sx={{
                                    bgcolor: '#DC143C',
                                    color: '#fff',
                                    borderRadius: '10px',
                                    py: '13px',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        bgcolor: '#b01030',
                                        transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                Add Details
                            </Button>
                        </Box>
                    </form>
                </FormikProvider>
            </DialogContent>
        </Dialog>
    );
};

export default AddressFormDialog;
